import {
	AssistantAvatarColor,
	type AssistantAvatarConfig,
	AssistantAvatarEyes,
	AssistantAvatarFacialHair,
	AssistantAvatarHair,
	AssistantAvatarHairColor,
	AssistantAvatarKind,
	AssistantAvatarShape,
	AssistantAvatarSkinTone,
	AssistantCharacterAccessory,
	type AssistantCharacterAvatar,
	AssistantHumanAccessory,
	type AssistantHumanAvatar,
} from "../../UIToolkit/AssistantAvatar/assistantAvatarConfig";

export type CharacterSlot = Exclude<keyof AssistantCharacterAvatar, "kind">;
export type HumanSlot = Exclude<keyof AssistantHumanAvatar, "kind">;
export type AnnieStudioSlot = CharacterSlot | HumanSlot;

/** Every value each slot accepts, in picker order. */
export const CHARACTER_SLOT_VALUES: { [Slot in CharacterSlot]: AssistantCharacterAvatar[Slot][] } = {
	shape: Object.values(AssistantAvatarShape),
	color: Object.values(AssistantAvatarColor),
	eyes: Object.values(AssistantAvatarEyes),
	accessory: Object.values(AssistantCharacterAccessory),
};

export const HUMAN_SLOT_VALUES: { [Slot in HumanSlot]: AssistantHumanAvatar[Slot][] } = {
	skin_tone: Object.values(AssistantAvatarSkinTone),
	hair: Object.values(AssistantAvatarHair),
	hair_color: Object.values(AssistantAvatarHairColor),
	facial_hair: Object.values(AssistantAvatarFacialHair),
	color: Object.values(AssistantAvatarColor),
	eyes: Object.values(AssistantAvatarEyes),
	accessory: Object.values(AssistantHumanAccessory),
};

const CHARACTER_SLOTS: readonly CharacterSlot[] = ["shape", "color", "eyes", "accessory"];
const HUMAN_SLOTS: readonly HumanSlot[] = ["skin_tone", "hair", "hair_color", "facial_hair", "color", "eyes", "accessory"];

/** The slots that differ between two designs; every slot when the style itself differs. */
export const changedSlots = (from: AssistantAvatarConfig, to: AssistantAvatarConfig): readonly AnnieStudioSlot[] => {
	if (to.kind === AssistantAvatarKind.Character) {
		return from.kind === AssistantAvatarKind.Character ? CHARACTER_SLOTS.filter((slot) => from[slot] !== to[slot]) : CHARACTER_SLOTS;
	}
	return from.kind === AssistantAvatarKind.Human ? HUMAN_SLOTS.filter((slot) => from[slot] !== to[slot]) : HUMAN_SLOTS;
};

export const isSameAvatar = (a: AssistantAvatarConfig, b: AssistantAvatarConfig): boolean => a.kind === b.kind && changedSlots(a, b).length === 0;

const pick = <T>(values: readonly T[], random: () => number): T => values[Math.floor(random() * values.length)];

const randomCharacter = (random: () => number): AssistantCharacterAvatar => ({
	kind: AssistantAvatarKind.Character,
	shape: pick(CHARACTER_SLOT_VALUES.shape, random),
	color: pick(CHARACTER_SLOT_VALUES.color, random),
	eyes: pick(CHARACTER_SLOT_VALUES.eyes, random),
	accessory: pick(CHARACTER_SLOT_VALUES.accessory, random),
});

const randomHuman = (random: () => number): AssistantHumanAvatar => ({
	kind: AssistantAvatarKind.Human,
	skin_tone: pick(HUMAN_SLOT_VALUES.skin_tone, random),
	hair: pick(HUMAN_SLOT_VALUES.hair, random),
	hair_color: pick(HUMAN_SLOT_VALUES.hair_color, random),
	facial_hair: pick(HUMAN_SLOT_VALUES.facial_hair, random),
	color: pick(HUMAN_SLOT_VALUES.color, random),
	eyes: pick(HUMAN_SLOT_VALUES.eyes, random),
	accessory: pick(HUMAN_SLOT_VALUES.accessory, random),
});

/** Draws until it lands on a different look, so "Surprise me" never appears to do nothing. */
export const randomAvatarLike = (current: AssistantAvatarConfig, random: () => number): AssistantAvatarConfig => {
	const draw = () => (current.kind === AssistantAvatarKind.Human ? randomHuman(random) : randomCharacter(random));
	let next = draw();
	while (isSameAvatar(next, current)) next = draw();
	return next;
};
